/** HTTP header name constants. Pure leaf so sibling const leaves can reference STANDARD_HEADERS / CUSTOM_HEADERS without triggering the `export` TDZ cycle the barrel would cause. */

export const STANDARD_HEADERS = {
	ACCEPT: 'Accept',
	ACCEPT_ENCODING: 'Accept-Encoding',
	ACCEPT_LANGUAGE: 'Accept-Language',
	ACCESS_CONTROL_REQUEST_HEADERS: 'Access-Control-Request-Headers',
	AUTHORIZATION: 'Authorization',
	CACHE_CONTROL: 'Cache-Control',
	CONNECTION: 'Connection',
	CONTENT_DISPOSITION: 'Content-Disposition',
	CONTENT_ENCODING: 'Content-Encoding',
	CONTENT_LENGTH: 'Content-Length',
	CONTENT_SECURITY_POLICY: 'Content-Security-Policy',
	CONTENT_TYPE: 'Content-Type',
	ETAG: 'ETag',
	IF_NONE_MATCH: 'If-None-Match',
	ORIGIN: 'Origin',
	PERMISSIONS_POLICY: 'Permissions-Policy',
	/** The requesting page's URL. Spelled with the historical single-R, which IS the wire field name -- a DIFFERENT
	 *  header from `REFERRER_POLICY` below, whose modern double-R spelling applies only to that policy field. Read (never
	 *  set) by the FlowRunner dev stack's two door guards beside `ORIGIN`: either one present proves the caller is a page. */
	REFERER: 'Referer',
	REFERRER_POLICY: 'Referrer-Policy',
	RETRY_AFTER: 'Retry-After',
	SEC_WEBSOCKET_PROTOCOL: 'Sec-WebSocket-Protocol',
	STRICT_TRANSPORT_SECURITY: 'Strict-Transport-Security',
	/**
	 * W3C Trace Context propagation header, lowercase because that spelling IS the spec's (the field name is defined
	 * lowercase, unlike the title-cased siblings above). Parsing and staging live in `const/w3c-trace-context.ts`; the
	 * name sits here so the inbound reader and every outbound forwarder name the field once.
	 */
	TRACEPARENT: 'traceparent',
	UPGRADE: 'Upgrade',
	USER_AGENT: 'User-Agent',
	VARY: 'Vary',
	WWW_AUTHENTICATE: 'WWW-Authenticate',
	X_CONTENT_TYPE_OPTIONS: 'X-Content-Type-Options',
	X_FRAME_OPTIONS: 'X-Frame-Options',
	X_XSS_PROTECTION: 'X-XSS-Protection',
} as const;

/**
 * Canonical VALUE strings for standard HTTP response headers -- companion to the KEY-side `STANDARD_HEADERS`. The earlier
 * header migration consolidated NAMES but left each emission surface its own inline value, producing runtime divergence
 * between the Worker `getSecurityHeaders` helper and the static `_headers` generator. `PERMISSIONS_POLICY` carries the broader 5-feature form; the widening is purely additive. Never re-inline a divergent string.
 */
export const STANDARD_HEADER_VALUES = {
	PERMISSIONS_POLICY: 'geolocation=(), microphone=(), camera=(), payment=(), usb=()',
	X_CONTENT_TYPE_OPTIONS: 'nosniff',
	X_FRAME_OPTIONS: 'DENY',
	X_XSS_PROTECTION: '1; mode=block',
	REFERRER_POLICY: 'strict-origin-when-cross-origin',
	STRICT_TRANSPORT_SECURITY: 'max-age=63072000; includeSubDomains; preload',
	USER_AGENT_CHROME_DESKTOP:
		'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
	/** The Accept-Language an ordinary US desktop browser sends; the RealAuction fetch, browser and probe clients all
	 *  present it so the three read to the site as one browser. */
	ACCEPT_LANGUAGE_EN_US: 'en-US,en;q=0.9',
} as const;

/** The request headers an ordinary US desktop browser sends: the caller's User-Agent plus US-English Accept-Language.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes this leaf's own header names with its own values. */
export function desktopBrowserRequestHeaders(userAgent: string): Record<string, string> {
	return { [STANDARD_HEADERS.USER_AGENT]: userAgent, [STANDARD_HEADERS.ACCEPT_LANGUAGE]: STANDARD_HEADER_VALUES.ACCEPT_LANGUAGE_EN_US };
}

export const CUSTOM_HEADERS = {
	USER_ID: 'X-User-Id',
	USER_PHONE: 'X-User-Phone',
	USER_EMAIL: 'X-User-Email',
	USER_NAME: 'X-User-Name',
	USER_ROLE: 'X-User-Role',
	API_TOKEN: 'X-API-Token',
	/**
	 * SERVICE-INTERNAL origin claim, compared against `SERVICE_NAME` by the two DO creation routes (PropertyDO
	 * `/createProperty`, UserDO `/createUser`). Its only producer is `buildDoPostOptions`, which rides a direct
	 * Durable-Object stub binding, so the name reaches a DO only over a DO-to-DO hop. It is a member of
	 * `INTERNAL_IDENTITY_HEADERS`, which deletes it off every request crossing a worker edge -- that strip is what makes
	 * the claim proof of internal origin rather than an ordinary header any caller could set.
	 */
	SOURCE: 'X-Source',
	CONNECTION_TYPE: 'X-Connection-Type',
	CALLER_TYPE: 'X-Caller-Type',
	CALLER_PAGE: 'X-Caller-Page',
	CALLER_COMPONENT: 'X-Caller-Component',
	CALLER_ACTION: 'X-Caller-Action',
	APP_NAME: 'X-App-Name',
	CONVERSATION_ID: 'X-Conversation-ID',
	SECRET: 'X-Secret-Header',
	STRIPE_SIGNATURE: 'stripe-signature',
	/** Outbound Stripe API-version pin (`STRIPE_API_VERSION`, `const/payments.ts`): without it every payload shape floats on the ACCOUNT default version and an account upgrade shifts wire shapes silently. */
	STRIPE_VERSION: 'Stripe-Version',
	/** Twilio's request signature: base64 HMAC-SHA1 over the signed URL plus the sorted POST params, keyed by the account auth token. The ONLY proof an inbound-webhook POST came from Twilio, so the surface reading it fails CLOSED when it is absent. */
	TWILIO_SIGNATURE: 'X-Twilio-Signature',
	X_STORAGE_TOKEN: 'x-storage-token',
	REQUEST_ID: 'X-Request-ID',
	/** THE INSTANT A MISSION BOARD PANE ANSWER WAS COMPOSED, epoch ms, set by the board server on every pane answer and
	 *  its 304 (`sendPaneJson`, `spa/mission/server/plugin-http.ts`): the one date the page stamps a read with, never the
	 *  browser's clock at the fetch (standards-auditor, 2026-09-23). */
	READ_AT_MS: 'X-Read-At-Ms',
	/** THE FIRST CHANGE A MISSION BOARD PANE ANSWER HAS NOT RE-READ, epoch ms: set only on an answer served from its
	 *  memo's held copy while a re-read has run past the board's bound (`plugin-pane-routes.ts`), so the page can say how
	 *  long its changes have not been shown. Absent on every current answer (standards-auditor, 2026-09-23). */
	READ_BEHIND_SINCE_MS: 'X-Read-Behind-Since-Ms',
	SESSION_ID: 'X-Session-ID',
	/**
	 * SERVER-MINTED identity channel: set ONLY by a worker edge that has just verified a JWT, and consumed by the
	 * Durable Objects as a PROVEN user id. It is deliberately a DIFFERENT name from `USER_ID`, which any client may
	 * send: `INTERNAL_IDENTITY_HEADERS` (`@federa/utils/websocket/ws-identity-channel`) deletes this name -- along with
	 * every other member of that list -- off every inbound request before the edge re-sets it, so an inbound copy can
	 * never survive to a DO.
	 */
	VERIFIED_USER_ID: 'X-Federa-Verified-User-Id',
} as const;

/**
 * Every header name carrying identity INTO a Durable Object. Membership is the whole contract: a name added to the DO
 * read side and NOT added here becomes a spoofable channel the moment it ships. `stripInboundIdentityHeaders`
 * (`@federa/utils/websocket/ws-identity-channel`) deletes every member off an inbound request, and
 * `buildDurableObjectRequest` is the only constructor of a DO-bound request, so the strip always runs.
 */
export const INTERNAL_IDENTITY_HEADERS: readonly string[] = [
	CUSTOM_HEADERS.VERIFIED_USER_ID,
	CUSTOM_HEADERS.USER_ID,
	CUSTOM_HEADERS.CONNECTION_TYPE,
	// SOURCE is a member because two DO routes gate on it as a SERVICE-INTERNAL claim -- PropertyDO's `/createProperty`
	// and UserDO's `/createUser`, both comparing it against `SERVICE_NAME`. Its sole producer is `buildDoPostOptions`,
	// used over a direct Durable-Object stub binding that never traverses this edge, so stripping the inbound copy costs
	// the legitimate caller nothing and is what makes the claim unforgeable: the name survives only when a sibling DO set
	// it, exactly as CONNECTION_TYPE already works.
	CUSTOM_HEADERS.SOURCE,
];
